import api from "@/lib/axiosConfig";
import { Auction } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { RelativePosition } from "../dtos";

export const baseRoute = "back/api/auctions";

interface AuctionDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

async function getAuction(id: string) {
  const { data } = await api.get<Auction>(`back/api/auctions/${id}`);
  return data;
}

export function useGetAuction(id?: string) {
  return useQuery({
    queryKey: ["auction", id],
    queryFn: () => (id ? getAuction(id) : undefined),
    placeholderData: keepPreviousData,
  });
}

async function getAuctions({
  where,
  order,
  take,
  skip,
}: AuctionDTO): Promise<Auction[]> {
  const { data } = await api.post(
    `back/api/auctions?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useGetAllAuctions = ({ where, order, take, skip }: AuctionDTO) => {
  return useQuery({
    queryKey: ["auctions", where, order, take, skip],
    queryFn: () => getAuctions({ where, order, take, skip }),
  });
};

export async function getCountByStatus(where?: object) {
  const { data } = await api.post(
    `back/api/auctions/countByStatus`,
    { where },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "auction"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

async function updateAuction(auction: Auction) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = auction;
  return await api.patch(`back/api/auctions/${auction.id}`, {
    data: rest,
  });
}

export function useUpdateAuction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAuction,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["auction", v.id] });
      queryClient.invalidateQueries({ queryKey: ["auctions"] });
    },
  });
}

type CreateAuctionParams = {
  ventesDescriptionNumeroVente: string;
  ventesDescriptionTitre: string;
};
type CreateAuctionResponse = Pick<Auction, "id">;

const createAuction = async ({
  ventesDescriptionNumeroVente,
  ventesDescriptionTitre,
}: CreateAuctionParams) => {
  return await api
    .post<CreateAuctionResponse>("back/api/auctions/create", {
      ventesDescriptionNumeroVente,
      ventesDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreateAuction = (
  options?: UseMutationOptions<
    CreateAuctionResponse,
    Error,
    CreateAuctionParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createAuction,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["auctions"] });
      queryClient.invalidateQueries({ queryKey: ["auction", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "auction"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const getAllIds = async (where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids`, {
    where,
  });
  return data;
};
